<template>
  <div class="slider-loader-container">
    <div class="slider-loader-bar" :style="{ width: `${progress}%` }"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';

const props = defineProps({
  duration: {
    type: Number,
    default: 15000 // 15 seconds default
  },
  isActive: {
    type: Boolean,
    default: true
  },
  reset: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['completed']);

const progress = ref(0);
let animationId: number;
let startTime: number;

const animate = (timestamp: number) => {
  if (!startTime) startTime = timestamp;
  
  const elapsed = timestamp - startTime;
  progress.value = Math.min((elapsed / props.duration) * 100, 100);
  
  if (elapsed < props.duration && props.isActive) {
    animationId = requestAnimationFrame(animate);
  } else if (elapsed >= props.duration) {
    emit('completed');
  }
};

const startAnimation = () => {
  progress.value = 0;
  startTime = 0;
  if (props.isActive) {
    animationId = requestAnimationFrame(animate);
  }
};

watch(() => props.reset, (newVal) => {
  if (newVal) {
    startAnimation();
  }
});

watch(() => props.isActive, (newVal) => {
  if (newVal) {
    startAnimation();
  } else {
    cancelAnimationFrame(animationId);
  }
});

onMounted(() => {
  startAnimation();
});

onBeforeUnmount(() => {
  cancelAnimationFrame(animationId);
});
</script>

<style scoped>
.slider-loader-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  min-width: 285px;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.2);
  overflow: hidden;
  z-index: 10;
}

.slider-loader-bar {
  height: 3px;
  background-color: #fff;
  transition: width 0.1s linear;
}
</style> 